import { AppButton } from '@clickguard/design-system'
import { GDropdown } from 'components/basic-blocks'
import { ProductAccountDTO } from 'api-models'
import { Onboarding } from 'services/user'

const PhoneIcon = ({ className }: { className?: string }) => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 20 20"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		className={className}
	>
		<path
			d="M11.7081 4.99984C12.5221 5.15864 13.2701 5.55672 13.8565 6.14312C14.4429 6.72952 14.841 7.47756 14.9998 8.2915M11.7081 1.6665C13.3992 1.85437 14.9761 2.61165 16.18 3.81401C17.3839 5.01637 18.1431 6.59235 18.3331 8.28317M8.52228 11.5524C7.52097 10.5511 6.73031 9.41888 6.15031 8.21086C6.10042 8.10695 6.07548 8.055 6.05631 7.98926C5.98821 7.75563 6.03713 7.46875 6.17881 7.27089C6.21867 7.21521 6.2663 7.16758 6.36156 7.07232C6.6529 6.78098 6.79857 6.63531 6.89381 6.48883C7.25297 5.93642 7.25297 5.22427 6.89381 4.67186C6.79857 4.52538 6.6529 4.37971 6.36156 4.08837L6.19917 3.92598C5.7563 3.48311 5.53487 3.26168 5.29705 3.14139C4.82408 2.90217 4.26553 2.90217 3.79256 3.14139C3.55474 3.26168 3.33331 3.48311 2.89044 3.92598L2.75908 4.05735C2.31773 4.4987 2.09705 4.71937 1.92851 5.0194C1.74149 5.35232 1.60703 5.8694 1.60816 6.25125C1.60918 6.59537 1.67594 6.83056 1.80944 7.30093C2.52692 9.82876 3.88065 12.2141 5.87063 14.204C7.86061 16.194 10.2459 17.5478 12.7737 18.2652C13.2441 18.3987 13.4793 18.4655 13.8234 18.4665C14.2053 18.4677 14.7224 18.3332 15.0553 18.1462C15.3553 17.9776 15.576 17.7569 16.0173 17.3156L16.1487 17.1842C16.5916 16.7414 16.813 16.5199 16.9333 16.2821C17.1725 15.8091 17.1725 15.2506 16.9333 14.7776C16.813 14.5398 16.5916 14.3184 16.1487 13.8755L15.9863 13.7131C15.695 13.4218 15.5493 13.2761 15.4028 13.1809C14.8504 12.8217 14.1383 12.8217 13.5858 13.1809C13.4394 13.2761 13.2937 13.4218 13.0024 13.7131C12.9071 13.8084 12.8595 13.856 12.8038 13.8959C12.6059 14.0375 12.319 14.0865 12.0854 14.0184C12.0197 13.9992 11.9677 13.9743 11.8638 13.9244C10.6558 13.3444 9.5236 12.5537 8.52228 11.5524Z"
			style={{ stroke: 'var(--icon-primary)' }}
			strokeWidth="1.66667"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
)

const QuestionMarkIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="19"
		height="19"
		viewBox="0 0 19 19"
		fill="none"
	>
		<path
			d="M6.74159 6.66732C6.93751 6.11037 7.32421 5.64074 7.83321 5.3416C8.34222 5.04245 8.94066 4.9331 9.52257 5.03291C10.1045 5.13272 10.6323 5.43525 11.0125 5.88692C11.3927 6.33859 11.6008 6.91025 11.5999 7.50065C11.5999 9.16732 9.09992 10.0007 9.09992 10.0007M9.16659 13.334H9.17492M17.4999 9.16732C17.4999 13.7697 13.769 17.5007 9.16659 17.5007C4.56421 17.5007 0.833252 13.7697 0.833252 9.16732C0.833252 4.56494 4.56421 0.833984 9.16659 0.833984C13.769 0.833984 17.4999 4.56494 17.4999 9.16732Z"
			stroke="#8F88FF"
			stroke-width="1.66667"
			stroke-linecap="round"
			stroke-linejoin="round"
		/>
	</svg>
)

interface NeedHelpTagProps {
	isOpen: boolean
	onToggle: () => void
}

export const NeedHelpTag = ({ isOpen, onToggle }: NeedHelpTagProps) => {
	return (
		<button
			onClick={onToggle}
			className={`bg-brand-tint-bg border-b-0 rounded-bl-none rounded-br-none  border border-brand-tint-border rounded-lg px-3 py-[8px] mr-6 text-[12px] leading-[15px] font-normal text-t-coupon hover:bg-white transition-colors flex items-center gap-1 whitespace-nowrap ${
				isOpen ? 'bg-white' : ''
			}`}
		>
			Need help?
			<QuestionMarkIcon />
		</button>
	)
}

interface NeedHelpButtonProps {
	accountId?: string
	onUpdateAccount?: (updatedAccount: ProductAccountDTO) => void
	containerClassName?: string
}

export const NeedHelpButton = ({
	accountId,
	onUpdateAccount,
	containerClassName
}: NeedHelpButtonProps) => {
	const handleLetsSetItUpTogether = async () => {
		if (accountId) {
			try {
				const updatedAccount = await Onboarding.updateSetup(accountId, {
					onboardingPath: 'self-onboarding-converted'
				})
				if (onUpdateAccount && updatedAccount) {
					onUpdateAccount(updatedAccount)
				}
			} catch (err) {
				console.error('Failed to update onboarding path:', err)
			}
		}
		window.location.href =
			'https://meetings.hubspot.com/thabisile-khumalo/onboarding'
	}

	return (
		<div className={`${containerClassName || ''}`}>
			<GDropdown
				menuButton={
					<button className="bg-white border border-t-card-border rounded-full px-3 py-2 text-[14px] leading-[15px] font-normal text-t-default hover:bg-[#554BFD]/5 transition-colors">
						Need help?
					</button>
				}
				position="bottom-end"
			>
				{(close) => (
					<div className="bg-popover rounded-[16px] shadow-dropShadow pt-6 pb-8 px-6 w-[245px]">
						<div className="flex flex-col gap-4">
							<div className="pb-1">
								<p className="text-[20px] font-medium text-t-default leading-[26px] whitespace-nowrap">
									Need help setting up?
								</p>
							</div>

							<div className="text-[14px] text-t-email-blue font-[300] leading-4">
								<p className="mb-4">
									<span>&#10003; </span>
									<span className="font-bold">No-cost </span>
									<span>guided account setup</span>
								</p>
								<p className="mb-4">
									<span>&#10003; </span>
									<span className="font-bold">Tailored</span>
									<span> to your campaigns and traffic sources</span>
								</p>
								<p>
									<span>&#10003; </span>
									<span className="font-bold">Get protected faster </span>
									<span>
										- most accounts are fully configured in a single call
									</span>
								</p>
							</div>

							<div className="flex flex-col gap-4 items-center pt-4">
								<AppButton
									variant="marketing"
									size="md"
									label="Let's set it up together"
									leftIcon={<PhoneIcon className="h-4 w-4" />}
									onClick={() => {
										close?.()
										handleLetsSetItUpTogether()
									}}
								/>

								<p className="text-[13px] text-t-gray-checkout text-center leading-4">
									Book a 1:1 onboarding call
									<br />
									with a product specialist.
								</p>
							</div>
						</div>
					</div>
				)}
			</GDropdown>
		</div>
	)
}

interface NeedHelpPanelProps {
	accountId?: string
	onUpdateAccount?: (updatedAccount: ProductAccountDTO) => void
	onClose: () => void
}

export const NeedHelpPanel = ({
	accountId,
	onUpdateAccount,
	onClose
}: NeedHelpPanelProps) => {
	const handleLetsSetItUpTogether = async () => {
		if (accountId) {
			try {
				const updatedAccount = await Onboarding.updateSetup(accountId, {
					onboardingPath: 'self-onboarding-converted'
				})
				if (onUpdateAccount && updatedAccount) {
					onUpdateAccount(updatedAccount)
				}
			} catch (err) {
				console.error('Failed to update onboarding path:', err)
			}
		}
		window.location.href =
			'https://meetings.hubspot.com/thabisile-khumalo/onboarding'
	}

	return (
		<div className="bg-white rounded-[16px] shadow-lg pt-[24px] pb-[32px] px-[24px] w-full">
			<div className="flex flex-col gap-[16px] w-[250px]">
				<div className="pb-[4px]">
					<p className="text-[20px] font-medium text-t-default leading-[26px] whitespace-nowrap">
						Need help setting up?{' '}
					</p>
				</div>

				<div className="text-[16px] text-t-email-blue font-light leading-[16px]">
					<p className="mb-[16px]">
						<span>&#10003; </span>
						<span className="font-bold">No-cost </span>
						<span>guided account setup</span>
					</p>
					<p className="mb-[16px]">
						<span>&#10003; </span>
						<span className="font-bold">Tailored</span>
						<span> to your campaigns and traffic sources</span>
					</p>
					<p>
						<span>&#10003; </span>
						<span className="font-bold">Get protected faster </span>
						<span>- most accounts are fully configured in a single call</span>
					</p>
				</div>

				<div className="flex flex-col gap-[16px] items-center pt-[16px]">
					<AppButton
						variant="marketing"
						size="md"
						label="Let's set it up together"
						leftIcon={<PhoneIcon className="h-4 w-4" />}
						onClick={handleLetsSetItUpTogether}
					/>

					<p className="text-[13px] text-t-gray-checkout text-center leading-[16px]">
						Book a 1:1 onboarding call
						<br />
						with a product specialist.
					</p>
				</div>
			</div>
		</div>
	)
}
